import { useContext } from 'react'

import { UpdateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
} from 'tripPlanner/components/TripModal/TripItems/Edit/utils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import {
  AttractionItem,
  CustomItem,
  EventItem,
  NoteItem,
  RestaurantBarItem,
  ThingToDoItem,
  TripItem,
} from 'tripPlanner/types/tripItem'

export function customItemToUpdatePayload(
  item: CustomItem,
): UpdateTripItemRequest {
  const {
    id,
    tripId,
    createdAt,
    updatedAt,
    userUpdatedAt,
    createdBy,
    updatedBy,
    startUtcDateTime,
    // @ts-ignore - Doesn't exist on all of these types, but we want to filter it out if it's there
    endUtcDateTime,
    sortableName,
    // @ts-ignore - Doesn't exist on all of these types, but we want to filter it out if it's there
    status,
    summaryRankString,
    itineraryRankString,
    ...editableFields
  } = item

  if (
    editableFields.type === 'ATTRACTION' ||
    editableFields.type === 'RESTAURANT_BAR'
  ) {
    return {
      ...editableFields,
      ...extractEditableStartDateTimeStrings(editableFields),
    }
  } else {
    return {
      ...editableFields,
      ...extractEditableStartDateTimeStrings(editableFields),
      ...extractEditableEndDateTimeStrings(editableFields),
    }
  }
}

export const itemTypesWithNoEndDate = [
  'ATTRACTION',
  'RESTAURANT_BAR',
] satisfies Array<
  Exclude<TripItem, { endDate: moment.Moment | undefined }>['type']
>

export function itemIsInItinerary(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !!item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate
  )
}

export function itemIsVisibleInItinerary(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !!item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate &&
    item.startDate.isBetween(trip.startDate, trip.endDate, 'day', '[]')
  )
}

export function canAddItemToItineraryWithExistingDate(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate &&
    item.startDate.isBetween(trip.startDate, trip.endDate, 'day', '[]')
  )
}

export function isCustomSingleDateItem(
  item: TripItem,
): item is
  | AttractionItem
  | EventItem
  | RestaurantBarItem
  | ThingToDoItem
  | NoteItem {
  return (
    item.type === 'ATTRACTION' ||
    item.type === 'EVENT' ||
    item.type === 'RESTAURANT_BAR' ||
    item.type === 'THING_TO_DO' ||
    item.type === 'NOTE'
  )
}

export function useItemOutsideOfTripDates(item: TripItem) {
  const tripContext = useContext(TripContext)
  if (tripContext?.startDate && tripContext?.endDate && item.isInItinerary) {
    let isInValidStartDate = false
    let isInValidEndDate = false
    if (item.startDate) {
      isInValidStartDate =
        item.startDate < tripContext.startDate ||
        item.startDate > tripContext.endDate
    }
    if ('endDate' in item && item.endDate) {
      isInValidEndDate =
        item.endDate < tripContext.startDate ||
        item.endDate > tripContext.endDate
    }
    return isInValidStartDate || isInValidEndDate
  }
  return false
}
